import React, {
    useContext,
    useState,
    useRef
} from 'react';
import { parseISO, format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';

import { getCustomer, NotificationContext, handleError } from '../router'

import * as OPTIONS from "../option-fields";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils"

import AccountNavigation from './account-navigation';
import UpsellBlock from '../components/upsell-block';
import LogoutButton from '../components/logout-button';
import Addresses from './addresses';

let NewCustomerDetails = null;

const AccountDetails = () => {

    const {notification, setNotification} = useContext(NotificationContext);
    const { data: customer } = getCustomer();

    const [validationMessages, setValidationMessages] = useState(false);
    
    const [customerData, setCustomerData] = useState({
        fName: customer.firstName,
        sName: customer.lastName,
        email: customer.email,
        confirmEmail: customer.email,
        mobile: customer.mobile,
        birthDate: format(parseISO(customer.birthDate), 'yyyy-MM-dd') 
    });

    const emailInputRef = useRef(null);
        
    const patchCustomerDetails = useMutation({
        mutationFn: () => QUERIES.patchCustomerDetails(NewCustomerDetails),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
    })

    const validateFields = (data) => {
        if (!data) return;

        const messages = {};

        // Spektrix criteria
        if (data.email.length > 255 || data.confirmEmail.length > 255) messages.emails = "Email address must not be longer than 255 characters.";
        if (emailInputRef && emailInputRef.current && !emailInputRef.current.validity.valid) messages.emails = "Please provide a valid email address.";
        if (data.fName && data.fName.length > 30) messages.fName = "First name should not be longer than 30 characters.";
        if (data.sName && data.sName.length > 80) messages.sName = "Surname should not be longer than 80 characters.";

        // If empty
        if (!data.fName) messages.fName = "Please provide a First Name.";
        if (!data.sName) messages.sName = "Please provide a Surname.";
        if (data.email != data.confirmEmail) messages.emails = "Email addresses do not match.";
        if (!data.email || !data.confirmEmail) messages.emails = "Please provide matching email addresses.";
        if (data.password != data.confirmPassword) messages.passwords = "Passwords do not match.";
        if (!data.password || !data.confirmPassword) messages.passwords = "Please provide matching passwords.";
        if (!data.mobile) messages.mobile = "Please provide valid mobile number.";

        return messages;
    }

    const submitCustomerData = (e) => {
        if (!e) return;
        e.preventDefault();

        const messages = validateFields(customerData);
        setValidationMessages(messages);

        const has_messages = Object.keys(messages).length != 0;
        const all_messages_null = Object.values(messages).every(o => o === null);
        if (has_messages || !all_messages_null) return;

        // This is the customer in the spektrix format
        NewCustomerDetails = {
            FirstName: customerData.fName,
            LastName: customerData.sName,
            Email: customerData.email,
            Password: customerData.password,
            Mobile: customerData.mobile,
            BirthDate: UTILS.datePickerToISOString(customerData.birthDate),
        }

        patchCustomerDetails.mutateAsync();
    }

    return (
        
        <>
            <AccountNavigation />
            <div className='booking-pathway__container booking-pathway__account booking-pathway__account--details'>

                <div className='booking-pathway__main-content'>
                    <h1 className='booking-pathway__header'>My details</h1>

                    <form onSubmit={submitCustomerData} className='booking-pathway__account--details--form'>
                        <div className='customer-details-form'>
                            <div className='form-field--group form-field--group__name'>
                                    <div className={`form-field form-field__first-name ${validationMessages.fName && 'form-field--error'}`}>
                                        <label htmlFor="fName">First Name
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="text" 
                                                id="fName" 
                                                value={customerData.fName} 
                                                onChange={(e) => { 
                                                    setCustomerData({...customerData, fName: e.target.value}); 
                                                    setValidationMessages({...validationMessages, fName: null}); 
                                                }} 
                                                required />
                                        {validationMessages.fName &&
                                            <p className='validation-message'>{validationMessages.fName}</p>
                                        }
                                    </div>

                                    <div className={`form-field form-field__first-field__surname ${validationMessages.sName && 'form-field--error'}`}>
                                        <label htmlFor="sName">Surname
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="text" 
                                                id="sName" 
                                                value={customerData.sName} 
                                                onChange={(e) => { 
                                                    setCustomerData({...customerData, sName: e.target.value}); 
                                                    setValidationMessages({...validationMessages, sName: null}); 
                                                }} 
                                                required />
                                        {validationMessages.sName &&
                                            <p className='validation-message'>{validationMessages.sName}</p>
                                        }
                                    </div>
                            </div>


                            <div className='form-field--group form-field--group__email'>
                                <div className={`form-field form-field__email ${validationMessages.emails && 'form-field--error'}`}>
                                    <label htmlFor="email">Email address
                                        <span className="label__required">(required)</span>
                                    </label>
                                    <input type="email" 
                                            id="email" 
                                            value={customerData.email} 
                                            onChange={(e) => { 
                                                setCustomerData({...customerData, email: e.target.value}); 
                                                setValidationMessages({...validationMessages, emails: null}); 
                                            }} 
                                            ref={emailInputRef}
                                            required />
                                    {validationMessages.emails &&
                                        <p className='validation-message'>{validationMessages.emails}</p>
                                    }
                                </div>

                                <div className={`form-field form-field__confirm-email ${validationMessages.emails && 'form-field--error'}`}>
                                    <label htmlFor="emailConfirm">Confirm email address
                                        <span className="label__required">(required)</span>
                                    </label>
                                    <input type="email" 
                                            id="emailConfirm" 
                                            value={customerData.confirmEmail} 
                                            onChange={(e) => { 
                                                setCustomerData({...customerData, confirmEmail: e.target.value}); 
                                                setValidationMessages({...validationMessages, emails: null}); 
                                            }}
                                            required />
                                    {validationMessages.emails &&
                                        <p className='validation-message'>{validationMessages.emails}</p>
                                    }
                                </div>
                            </div>

                            <div className='form-field--group form-field--group__phone'>
                                <div className={`form-field form-field__mobile ${validationMessages.mobile && 'form-field--error'}`}>
                                    <label htmlFor="mobile">Mobile
                                        <span className="label__required">(required)</span>
                                    </label>
                                    <input type="tel" 
                                            id="mobile" 
                                            value={customerData.mobile} 
                                            onChange={(e) => { 
                                                setCustomerData({...customerData, mobile: e.target.value}); 
                                                setValidationMessages({...validationMessages, mobile: null}); 
                                            }}
                                            required />
                                    {validationMessages.mobile &&
                                        <p className='validation-message'>{validationMessages.mobile}</p>
                                    }
                                </div>
                            </div>

                            <div className='form-field--group form-field--group__birthdate'>
                                <div className={`form-field form-field__birthdate ${validationMessages.birthDate && 'form-field--error'}`}>
                                    <label htmlFor="birthDate">Date of birth
                                        <span className="label__required">(required)</span>
                                    </label>
                                    <input type="date" 
                                            id="birthDate" 
                                            value={customerData.birthDate} 
                                            onChange={(e) => { 
                                                setCustomerData({...customerData, birthDate: e.target.value}); 
                                                setValidationMessages({...validationMessages, birthDate: null}); 
                                            }}
                                            required />
                                    {validationMessages.birthDate &&
                                        <p className='validation-message'>{validationMessages.birthDate}</p>
                                    }
                                </div>
                            </div>
                                
                        </div>
                    </form>

                    <Addresses />              
                </div>

                <div className='booking-pathway__footer'>
                    <LogoutButton />

                    <UpsellBlock content={OPTIONS.account.help_upsell} type="help-upsell" />
                </div>

            </div>
        </>
    );
};

export default AccountDetails;
